import { useEffect, useMemo, useState } from 'react'
import { useSticky } from 'react-table-sticky'
import PropTypes from 'prop-types'
// noinspection ES6CheckImport
import { useBlockLayout, useTable } from 'react-table'
import classNames from 'classnames'
import DashboardLoading from 'features/dashboard/table/shared/DashboardLoading'
import { useDispatch, useSelector } from 'react-redux'
import { useErrorToast } from 'utils/hooks/useErrorToast'
import { useDashboardTableSelector } from 'features/dashboard/utils/hooks/useDashboardTableSelector'
import { useDashboardTableActions } from 'features/dashboard/utils/hooks/useDashboardTableActions'
import EmptyTable from 'features/dashboard/table/shared/EmptyTable'
import useWindowDimensions from 'utils/hooks/useWindowDimensions'
import useTableColumns from 'features/dashboard/table/column/useTableColums'
import { CampaignTypes } from '../../Codes'

/**
 * @file React Table
 * @see https://react-table.tanstack.com
 * @see https://github.com/GuillaumeJasmin/react-table-sticky
 */

const DashboardRow = ({ row, selectedIds }) => {
  const {
    original: { id, deleted = false },
  } = row
  const { key: rowKey, ...rowProps } = row.getRowProps()
  const [hover, setHover] = useState(false)
  return (
    <div
      key={rowKey}
      {...rowProps}
      className={classNames('tr', {
        on: selectedIds.includes(id),
        over: hover,
        tr_inactive: deleted,
      })}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}>
      {row.cells.map(cell => {
        const { key: cellKey, ...cellProps } = cell.getCellProps()
        const lastFixed = cellProps['data-sticky-last-left-td']
        return (
          <div
            key={cellKey}
            {...cellProps}
            className={classNames('td', { cell_fixed: lastFixed })}>
            {cell.render('Cell')}
          </div>
        )
      })}
    </div>
  )
}
DashboardRow.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object,
    getRowProps: PropTypes.func,
    cells: PropTypes.array,
  }),
  selectedIds: PropTypes.array,
}
const DashboardTable = () => {
  const dispatch = useDispatch()
  const { ref: tableRef, columns } = useTableColumns()
  const { height: windowHeight } = useWindowDimensions()
  const { visible: infoVisible } = useSelector(state => state.dashboard.info)

  const { content, selectedIds, isLoading, error, editable } =
    useDashboardTableSelector()
  const tableHeight = useMemo(
    () => (infoVisible ? 885 : windowHeight - 333),
    [infoVisible, windowHeight]
  )
  const data = useMemo(
    () =>
      editable
        ? content.filter(
            ({ id, keywordType }) =>
              selectedIds.includes(id) &&
              keywordType === CampaignTypes.PREMIUM_LINK
          )
        : content,
    [editable, content, selectedIds]
  )
  const { clearError } = useDashboardTableActions()
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
      },
      useBlockLayout,
      useSticky
    )
  useErrorToast({
    error,
    handleClearError: () => dispatch(clearError()),
  })
  useEffect(() => {
    if (!isLoading && tableRef.current) {
      tableRef.current.scrollTop = 0
    }
  }, [isLoading, tableRef])
  const [scrolledX, setScrolledX] = useState(false)
  const [scrolledY, setScrolledY] = useState(false)
  const handleScroll = ({ target: { scrollLeft, scrollTop } }) => {
    setScrolledX(scrollLeft > 0)
    setScrolledY(scrollTop > 0)
  }

  return (
    <div className="table_wrap">
      <div
        className={classNames('table_dashboard', {
          scroll_left: scrolledX,
          scroll_top: scrolledY,
        })}>
        <DashboardLoading isLoading={isLoading} />
        <div
          className="table sticky"
          {...getTableProps()}
          style={{ height: tableHeight }}
          ref={tableRef}
          onScroll={handleScroll}>
          {data.length > 0 ? (
            <>
              <div className="header">
                {headerGroups.map(headerGroup => {
                  const { key: headerGroupKey, ...headerGroupRest } =
                    headerGroup.getHeaderGroupProps()
                  return (
                    <div
                      key={headerGroupKey}
                      className="tr"
                      {...headerGroupRest}>
                      {headerGroup.headers.map(column => {
                        const { key: headerKey, ...headerRest } =
                          column.getHeaderProps()
                        const lastFixed = headerRest['data-sticky-last-left-td']
                        return (
                          <div
                            key={headerKey}
                            className={classNames('th', {
                              cell_fixed: lastFixed,
                            })}
                            {...headerRest}>
                            {column.render('Header')}
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
              <div {...getTableBodyProps()} className="body">
                {rows.map(row => {
                  prepareRow(row)
                  const { key } = row.getRowProps()
                  return (
                    <DashboardRow
                      key={key}
                      row={row}
                      selectedIds={selectedIds}
                    />
                  )
                })}
              </div>
            </>
          ) : (
            <EmptyTable />
          )}
        </div>
      </div>
    </div>
  )
}
export default DashboardTable
